"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Heading, Input, Text, Textarea } from "@infinaut/ui";
import {
	Checkbox,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@infinaut/ui/client";
import { Turnstile } from "@marsidev/react-turnstile";
import Image from "next/image";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import type { InquiryFormValues } from "@/src/app/types";
import { inquiryFormSchema } from "@/src/app/types";
import { marketingConsentContent, privacyPolicyContent } from "./content";

const ContactForm = () => {
	const [isFormInteracted, setIsFormInteracted] = useState(false);
	const handleInteraction = () => {
		if (!isFormInteracted) {
			setIsFormInteracted(true);
		}
	};

	const form = useForm<InquiryFormValues>({
		resolver: zodResolver(inquiryFormSchema),
		defaultValues: {
			inquiryType: "car",
			inquirerName: "",
			inquirerPhone: "",
			inquiryMessage: "",
			isPrivacyPolicyAgreed: false,
			isMarketingConsentAgreed: false,
			turnstileToken: "",
			inquiryDetails: {
				interestedCarModel: "",
			},
		},
	});
	const {
		formState: { isSubmitting },
	} = form;

	const onSubmit = async (values: InquiryFormValues) => {
		try {
			const workerApiUrl =
				process.env.NODE_ENV === "production"
					? `${process.env.NEXT_PUBLIC_CLOUDFLARE_WORKER_API_URL}/inquiries`
					: "http://localhost:8787/inquiries";

			const response = await fetch(workerApiUrl, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					"X-Custom-Auth-Key":
						process.env.NEXT_PUBLIC_CLOUDFLARE_WORKER_API_KEY ?? "",
				},
				body: JSON.stringify(values),
			});

			if (!response.ok) {
				const err = (await response.json()) as { message?: string };
				throw new Error(err.message);
			}

			toast.success("문의가 성공적으로 제출되었습니다.");
			form.reset();
		} catch (error) {
			let errorMessage = "문의 제출 중 오류가 발생했습니다. 다시 시도해주세요.";
			if (error instanceof Error) {
				errorMessage = error.message;
			}
			toast.error(errorMessage);
		}
	};

	return (
		<section
			id="contact"
			className="py-24 bg-sidebar border-t border-border scroll-mt-28"
		>
			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
				<div className="bg-background rounded-3xl overflow-hidden border border-border shadow-2xl flex flex-col md:flex-row">
					{/* Left Column: Consultant Image */}
					<div className="relative w-full md:w-5/12">
						<Image
							src="/images/manager.webp"
							alt="Professional Consultant"
							width={600}
							height={800}
							className="w-full h-full object-cover"
							unoptimized
						/>
						{/* Gradient Overlay - Adjusted to match background card background */}
						<div className="absolute inset-0 bg-linear-to-t md:bg-linear-to-r from-background via-background/20 to-transparent" />

						<div className="absolute bottom-0 left-0 w-full p-8 z-10">
							<Heading
								level={3}
								className="text-3xl font-black text-foreground mb-1"
							>
								김현우 팀장
							</Heading>
							<Text className="text-primary font-semibold text-sm mb-4">
								프라임렌트카 수석 카매니저
							</Text>
							<Text
								as="blockquote"
								className="text-foreground text-sm font-light leading-relaxed border-l-2 border-primary pl-4 bg-sidebar/80 backdrop-blur-sm py-2 pr-2 rounded-r-lg"
							>
								"고객님의 라이프스타일에 딱 맞는
								<br />
								최적의 차량과 금융 조건을 설계해드립니다."
							</Text>
						</div>
					</div>

					{/* Right Column: Form */}
					<div className="w-full md:w-7/12 p-8 md:p-12 lg:p-16 bg-background">
						<div className="mb-8">
							<Heading
								level={2}
								className="text-3xl font-black tracking-tight text-primary mb-3"
							>
								무료 비교견적 신청
							</Heading>
							<Text variant="lead">
								원하시는 차종의 최저가 견적을 <br className="md:hidden" />
								빠르고 정확하게 안내해 드립니다.
							</Text>
						</div>

						<Form {...form}>
							<form
								className="space-y-2"
								onSubmit={form.handleSubmit(onSubmit)}
							>
								<div className="grid grid-cols-1 md:grid-cols-2 gap-5">
									<FormField
										control={form.control}
										name="inquirerName"
										render={({ field }) => (
											<FormItem>
												<FormLabel className="text-muted-foreground">
													이름
												</FormLabel>
												<FormControl>
													<Input
														type="text"
														placeholder="홍길동"
														className="bg-sidebar border-border placeholder:text-muted-foreground rounded-xl"
														{...field}
														onFocus={handleInteraction}
													/>
												</FormControl>
												<div className="h-5">
													<FormMessage />
												</div>
											</FormItem>
										)}
									/>
									<FormField
										control={form.control}
										name="inquirerPhone"
										render={({ field }) => (
											<FormItem>
												<FormLabel className="text-muted-foreground">
													연락처
												</FormLabel>
												<FormControl>
													<Input
														type="tel"
														placeholder="010-0000-0000"
														className="bg-sidebar border-border placeholder:text-muted-foreground rounded-xl"
														{...field}
														onFocus={handleInteraction}
													/>
												</FormControl>
												<div className="h-5">
													<FormMessage />
												</div>
											</FormItem>
										)}
									/>
								</div>

								<FormField
									control={form.control}
									name="inquiryDetails.interestedCarModel"
									render={({ field }) => (
										<FormItem>
											<FormLabel className="text-muted-foreground">
												관심 차종
											</FormLabel>
											<FormControl>
												<Input
													type="text"
													placeholder="예) 현대 그랜저, 제네시스 GV80"
													className="bg-sidebar border-border placeholder:text-muted-foreground rounded-xl"
													{...field}
													onFocus={handleInteraction}
												/>
											</FormControl>
											<div className="h-5">
												<FormMessage />
											</div>
										</FormItem>
									)}
								/>

								<FormField
									control={form.control}
									name="inquiryMessage"
									render={({ field }) => (
										<FormItem>
											<FormLabel className="text-muted-foreground">
												문의 내용 (선택)
											</FormLabel>
											<FormControl>
												<Textarea
													rows={3}
													placeholder="궁금하신 내용을 자유롭게 적어주세요."
													className="bg-sidebar border-border placeholder:text-muted-foreground resize-none rounded-xl"
													{...field}
													onFocus={handleInteraction}
												/>
											</FormControl>
											<div className="h-5">
												<FormMessage />
											</div>
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="isPrivacyPolicyAgreed"
									render={({ field }) => (
										<FormItem className="flex flex-row items-center space-x-3 space-y-0 py-2">
											<FormControl>
												<Checkbox
													checked={field.value}
													onCheckedChange={field.onChange}
													className="w-5 h-5 rounded border-border bg-sidebar text-primary focus:ring-primary cursor-pointer data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground"
												/>
											</FormControl>
											<div className="space-y-1 leading-none">
												<FormLabel className="text-sm text-muted-foreground cursor-pointer select-none hover:text-foreground">
													개인정보 수집 및 이용에 동의합니다.
													<Dialog>
														<DialogTrigger asChild>
															<Button
																variant="link"
																className="p-1 h-auto hover:cursor-pointer text-primary"
															>
																[내용 보기]
															</Button>
														</DialogTrigger>
														<DialogContent className="bg-sidebar border-border text-foreground">
															<DialogHeader>
																<DialogTitle>
																	개인정보 수집 및 이용 동의
																</DialogTitle>
															</DialogHeader>
															<DialogDescription className="max-h-[50vh] overflow-y-auto bg-background p-4 rounded-lg border-muted">
																<Text
																	as="pre"
																	className="text-sm font-sans whitespace-pre-wrap text-muted-foreground"
																>
																	{privacyPolicyContent}
																</Text>
															</DialogDescription>
														</DialogContent>
													</Dialog>
												</FormLabel>
											</div>
										</FormItem>
									)}
								/>

								<FormField
									control={form.control}
									name="isMarketingConsentAgreed"
									render={({ field }) => (
										<FormItem className="flex flex-row items-center space-x-3 space-y-0 py-2">
											<FormControl>
												<Checkbox
													checked={field.value}
													onCheckedChange={field.onChange}
													className="w-5 h-5 rounded border-border bg-sidebar text-primary focus:ring-primary cursor-pointer data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground"
												/>
											</FormControl>
											<div className="space-y-1 leading-none">
												<FormLabel className="text-sm text-muted-foreground cursor-pointer select-none hover:text-foreground">
													마케팅 정보 수신 및 광고성 정보 전송에 동의합니다.
													(선택)
													<Dialog>
														<DialogTrigger asChild>
															<Button
																variant="link"
																className="p-1 h-auto hover:cursor-pointer text-primary"
															>
																[내용 보기]
															</Button>
														</DialogTrigger>
														<DialogContent className="bg-sidebar border-border text-foreground">
															<DialogHeader>
																<DialogTitle>
																	마케팅 정보 수신 및 광고성 정보 전송 동의
																</DialogTitle>
															</DialogHeader>
															<DialogDescription className="max-h-[50vh] overflow-y-auto bg-background p-4 rounded-lg border-muted">
																<Text
																	as="pre"
																	className="text-sm font-sans whitespace-pre-wrap text-muted-foreground"
																>
																	{marketingConsentContent}
																</Text>
															</DialogDescription>
														</DialogContent>
													</Dialog>
												</FormLabel>
											</div>
										</FormItem>
									)}
								/>
								<div className="h-25">
									{isFormInteracted && (
										<FormField
											control={form.control}
											name="turnstileToken"
											render={({ field }) => (
												<FormItem>
													<FormControl>
														<Turnstile
															siteKey={
																process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY ?? ""
															}
															onSuccess={(token) => {
																field.onChange(token);
															}}
															onError={() => {
																field.onChange("");
															}}
														/>
													</FormControl>
													<div className="h-5">
														<FormMessage />
													</div>
												</FormItem>
											)}
										/>
									)}
								</div>

								<Button
									type="submit"
									disabled={isSubmitting}
									className="w-full bg-primary text-primary-foreground font-bold py-4 h-auto rounded-xl transition-all transform hover:cursor-pointer hover:scale-[1.01] hover:bg-primary/90 active:scale-[0.99] flex items-center justify-center gap-2"
								>
									{isSubmitting ? "전송 중..." : "무료 견적 신청하기"}
								</Button>
							</form>
						</Form>
					</div>
				</div>
			</div>
		</section>
	);
};

export default ContactForm;
